import { Fragment, useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { ComparacaoSelect } from '../components/ComparacaoSelect'
import { DomainPageShell } from '../components/DomainPageShell'
import { KpiCard } from '../components/KpiCard'
import { PropostaIaCard, type PropostaIaState } from '../components/PropostaIaCard'
import { usePanelInsightsFoco, useRegisterPanelInsights } from '../state/PanelInsightsContext'
import { useBiData } from '../state/BiDataContext'
import {
  canalFilterLabel,
  fetchProdutosVendidos,
  fetchVariacoesAnuncio,
  fmtBrl,
  pedirAnaliseProposta,
} from '../api/bridge'
import {
  janelaComparacao,
  labelModo,
  rotularJanela,
  variacaoPct,
  type Janela,
  type ModoComparacao,
} from './home/comparacao'
import {
  montarResumo,
  normalizarLinhas,
  type LinhaAnuncio,
  type ResumoAnuncios,
} from './vendasAnuncio/logic'

const POR_PAGINA = 50
type Ordem = 'receita' | 'quantidade'

const VAZIO: ResumoAnuncios = { anuncios: 0, unidades: 0, receita: 0, precoMedio: null }

function fmtInt(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString('pt-BR')
}

function leituraLocalVendas(
  resumo: ResumoAnuncios,
  comparado: ResumoAnuncios | null,
  top: LinhaAnuncio[],
  excluidos: { pedidos: number; receita: number } | null,
): string {
  const linhas: string[] = []
  linhas.push(
    `${fmtInt(resumo.anuncios)} anúncios com venda · ${fmtInt(resumo.unidades)} un · ${fmtBrl(resumo.receita)}` +
      (resumo.precoMedio != null ? ` · preço médio ${fmtBrl(resumo.precoMedio)}` : ''),
  )
  if (comparado) {
    const dRec = variacaoPct(resumo.receita, comparado.receita)
    const dUn = variacaoPct(resumo.unidades, comparado.unidades)
    if (dRec != null) linhas.push(`Receita vs comparação: ${dRec > 0 ? '+' : ''}${dRec.toFixed(1)}%`)
    if (dUn != null) linhas.push(`Unidades vs comparação: ${dUn > 0 ? '+' : ''}${dUn.toFixed(1)}%`)
  }
  const top3 = top.slice(0, 3).filter((l) => l.receita > 0)
  if (top3.length) {
    linhas.push(
      'Top por receita: ' +
        top3.map((l) => `${l.titulo || l.id} (${fmtBrl(l.receita)})`).join('; '),
    )
  }
  if (excluidos) {
    linhas.push(
      `Fora do cálculo: ${fmtInt(excluidos.pedidos)} pedidos multi-anúncio (${fmtBrl(excluidos.receita)}).`,
    )
  }
  linhas.push('Verdade = KPIs e tabela. Isto é só leitura local se a IA não responder.')
  return linhas.join('\n')
}

type LinhaVariacao = { chave: string; pedidos: number; unidades: number; receita: number }
type EstadoVariacoes = { carregando: boolean; linhas: LinhaVariacao[]; erro: string | null }

/**
 * Detalhe do anúncio por variação, dentro da linha expandida.
 *
 * A variação aparece pela **combinação de SKUs** que a base registra no pedido. O nome da cor
 * não vem daí — só da API do canal — então a tela não batiza a linha de "azul".
 */
function PainelVariacoes({ estado }: { estado: EstadoVariacoes | undefined }) {
  if (!estado || estado.carregando) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Carregando variações…
        </Typography>
      </Stack>
    )
  }
  if (estado.erro) {
    return (
      <Typography variant="caption" color="warning.main">
        {estado.erro}
      </Typography>
    )
  }
  if (!estado.linhas.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sem variação registrada neste período — o anúncio vendeu numa combinação só.
      </Typography>
    )
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        {estado.linhas.length} variação(ões) no período · identificadas pela combinação de SKUs
        do pedido (o nome da cor não está na base)
      </Typography>
      <Table size="small">
        <TableBody>
          {estado.linhas.map((v) => (
            <TableRow key={v.chave}>
              <TableCell sx={{ border: 0, py: 0.4 }}>
                <Typography variant="body2">{v.chave}</Typography>
              </TableCell>
              <TableCell
                align="right"
                sx={{ border: 0, py: 0.4, fontVariantNumeric: 'tabular-nums', width: 130 }}
              >
                {fmtInt(v.pedidos)} ped.
              </TableCell>
              <TableCell
                align="right"
                sx={{ border: 0, py: 0.4, fontVariantNumeric: 'tabular-nums', width: 130 }}
              >
                {fmtInt(v.unidades)} un.
              </TableCell>
              <TableCell
                align="right"
                sx={{ border: 0, py: 0.4, fontVariantNumeric: 'tabular-nums', width: 150 }}
              >
                {fmtBrl(v.receita)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

/**
 * Vendas por Anúncio — a tela "Por Anúncio" do Upseller, que a Mariana usa (print de
 * 17/09/2026). Substitui a antiga página Produtos, por decisão dela na mesma data.
 *
 * Filtro de período e de loja continuam sendo os globais da barra de cima; por padrão vêm
 * **todas as contas**, que é como ela pediu que esta tela abrisse.
 */
export function ProdutosPage() {
  const { periodQuery, marketplace, canalSelect, data: overview } = useBiData()
  const canalLabel = canalFilterLabel(canalSelect)

  const [modo, setModo] = useState<ModoComparacao>('anterior')
  const [pagina, setPagina] = useState(1)
  const [ordem, setOrdem] = useState<Ordem>('receita')
  const [draftBusca, setDraftBusca] = useState('')
  const [busca, setBusca] = useState('')

  const [linhas, setLinhas] = useState<LinhaAnuncio[]>([])
  const [resumo, setResumo] = useState<ResumoAnuncios>(VAZIO)
  const [totalPaginas, setTotalPaginas] = useState(1)
  const [comparado, setComparado] = useState<ResumoAnuncios | null>(null)
  const [janela, setJanela] = useState<Janela | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [aberto, setAberto] = useState<string | null>(null)
  const [variacoes, setVariacoes] = useState<Record<string, EstadoVariacoes>>({})
  const [excluidos, setExcluidos] = useState<{ pedidos: number; receita: number } | null>(null)
  const [leituraIa, setLeituraIa] = useState<PropostaIaState>({ loading: false })

  const carregar = useCallback(async () => {
    setLoading(true)
    setError(null)
    const mp = marketplace || undefined
    // As datas efetivas saem da própria resposta (`periodo.inicio/fim`): com um preset como
    // "Últimos 7 dias" quem resolve o intervalo é o bridge, e o front não tem como saber
    // antes de perguntar. Por isso a janela de comparação só é montada depois desta chamada.
    let periodoResolvido: { inicio: string; fim: string } | null = null
    try {
      const atual = await fetchProdutosVendidos(periodQuery, mp, {
        pagina,
        limite: POR_PAGINA,
        busca: busca || undefined,
        ordenarPor: ordem,
      })
      setLinhas(normalizarLinhas(atual.produtos))
      setResumo(montarResumo(atual.resumo, atual.paginacao?.total))
      const pages = Number(atual.paginacao?.pages || 0)
      setTotalPaginas(pages > 0 ? pages : 1)
      const ex = atual.excluidos_multi_anuncio
      setExcluidos(
        ex && Number(ex.pedidos || 0) > 0
          ? { pedidos: Number(ex.pedidos), receita: Number(ex.receita || 0) }
          : null,
      )
      if (atual.periodo?.inicio && atual.periodo?.fim) {
        periodoResolvido = { inicio: atual.periodo.inicio, fim: atual.periodo.fim }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível carregar os anúncios.')
      setLinhas([])
      setResumo(VAZIO)
      setTotalPaginas(1)
    } finally {
      setLoading(false)
    }

    const alvo = periodoResolvido
      ? janelaComparacao(periodoResolvido.inicio, periodoResolvido.fim, modo)
      : null
    setJanela(alvo)
    // A comparação é uma segunda chamada, só pelo resumo: `limite: 1` porque as linhas da
    // janela anterior não vão para a tela, só os totais dos KPIs. Falhou, a tela mostra o
    // período sem variação — nunca um zero que passaria por queda de 100%.
    if (!alvo) {
      setComparado(null)
      return
    }
    try {
      const antes = await fetchProdutosVendidos(
        { dataInicio: alvo.inicio, dataFim: alvo.fim },
        mp,
        { pagina: 1, limite: 1, busca: busca || undefined, ordenarPor: ordem },
      )
      setComparado(montarResumo(antes.resumo, antes.paginacao?.total))
    } catch {
      setComparado(null)
    }
  }, [periodQuery, marketplace, pagina, busca, ordem, modo])

  useEffect(() => {
    carregar()
  }, [carregar])

  // Filtro, período ou ordenação mudaram: a página 3 do recorte anterior não existe mais.
  useEffect(() => {
    setPagina(1)
  }, [periodQuery, marketplace, busca, ordem])

  // Outro recorte, outros números: o detalhe por variação que estava em cache é do recorte
  // antigo e mentiria se continuasse aberto.
  useEffect(() => {
    setAberto(null)
    setVariacoes({})
  }, [periodQuery, marketplace, busca])

  const rotuloJanela = rotularJanela(janela)
  const sufixo = `vs ${labelModo(modo).toLowerCase().replace('mesmo período do ', '')}`

  const { focoRef } = usePanelInsightsFoco()
  const carregarLeitura = useCallback(async () => {
    if (error || loading) return
    setLeituraIa((prev) => ({ ...prev, loading: true, erro: null }))
    const fallback = leituraLocalVendas(resumo, comparado, linhas, excluidos)
    const data = await pedirAnaliseProposta({
      tipo: 'insight_reflexao',
      insight: {
        dominio: 'vendas_por_anuncio',
        dominio_label: 'Vendas por Anúncio',
        titulo: 'Leitura do mix por anúncio',
        leitura: fallback,
        ...(focoRef.current ? { foco_usuario: focoRef.current } : {}),
        periodo: overview?.rangeLabel || null,
        metricas: {
          anuncios: resumo.anuncios,
          unidades: resumo.unidades,
          receita: resumo.receita,
          preco_medio: resumo.precoMedio,
          delta_receita_pct: comparado ? variacaoPct(resumo.receita, comparado.receita) : null,
          delta_unidades_pct: comparado ? variacaoPct(resumo.unidades, comparado.unidades) : null,
          excluidos_multi_anuncio: excluidos,
          top: linhas.slice(0, 5).map((l) => ({
            id: l.id,
            titulo: l.titulo || l.descricao,
            receita: l.receita,
            unidades: l.unidades,
          })),
        },
      },
    })
    if (!data.ok) {
      setLeituraIa({
        loading: false,
        texto: fallback,
        fonte: 'regras',
        modelo: null,
        erro: null,
      })
      return
    }
    setLeituraIa({
      loading: false,
      texto: data.leitura || data.texto || fallback,
      modelo: data.modelo,
      fonte: data.fonte,
      cached: data.cached,
      gerado_em: data.gerado_em,
    })
  }, [error, loading, resumo, comparado, linhas, excluidos, overview?.rangeLabel])

  useEffect(() => {
    if (loading || error) return
    void carregarLeitura()
  }, [loading, error, resumo.anuncios, resumo.receita, modo, carregarLeitura])

  useRegisterPanelInsights({
    titulo: 'Vendas por Anúncio',
    verdade: 'Verdade = margens type "pedido" por anúncio (KPIs e tabela desta tela). Proposta = leitura da IA — revise antes de agir.',
    estado: leituraIa,
    recarregar: carregarLeitura,
  })

  // A chamada falhou: os cards mostram "—", nunca zero. Zero aqui seria afirmar que o período
  // não teve venda, quando o que houve foi a tela não conseguir perguntar.
  const v = (texto: string) => (error ? '—' : texto)

  const aplicarBusca = () => {
    setPagina(1)
    setBusca(draftBusca.trim())
  }

  /**
   * Abre/fecha o detalhe por variação de um anúncio.
   *
   * Busca só na primeira abertura e guarda o resultado — reabrir não repete a chamada. O
   * cache morre quando muda período, canal ou busca, porque o mesmo anúncio em outro recorte
   * tem outros números (ver o `useEffect` abaixo).
   */
  const alternarVariacoes = async (id: string) => {
    if (aberto === id) {
      setAberto(null)
      return
    }
    setAberto(id)
    if (variacoes[id]) return
    setVariacoes((v) => ({ ...v, [id]: { carregando: true, linhas: [], erro: null } }))
    try {
      const r = await fetchVariacoesAnuncio(id, periodQuery, marketplace || undefined)
      setVariacoes((v) => ({
        ...v,
        [id]: {
          carregando: false,
          erro: null,
          linhas: (r.variacoes || []).map((x) => ({
            chave: String(x.chave || '—'),
            pedidos: Number(x.pedidos || 0),
            unidades: Number(x.quantidade || 0),
            receita: Number(x.receita || 0),
          })),
        },
      }))
    } catch {
      // O bridge oficial ainda não tem esta rota. Dizer isso é melhor do que uma lista vazia,
      // que se confunde com "este anúncio não tem variação".
      setVariacoes((v) => ({
        ...v,
        [id]: {
          carregando: false,
          linhas: [],
          erro: 'Este backend ainda não oferece o detalhe por variação.',
        },
      }))
    }
  }

  return (
    <DomainPageShell
      title="Vendas por Anúncio"
      subtitle={
        `${overview?.rangeLabel ? `${overview.rangeLabel} · ` : ''}` +
        `${marketplace ? canalLabel : 'todas as contas'}` +
        `${janela ? ` · comparado com ${rotuloJanela}` : ''}`
      }
      loading={loading}
      error={error}
      onRetry={carregar}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'flex-end' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Anúncios com venda no período, somando pedidos válidos. Um anúncio por linha, como
          ele existe no canal.
        </Typography>
        <ComparacaoSelect value={modo} onChange={setModo} disabled={loading} sx={{ minWidth: 260 }} />
      </Stack>

      <Grid container spacing={{ xs: 1.25, sm: 2 }} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <KpiCard
            label="Anúncios com venda"
            value={v(fmtInt(resumo.anuncios))}
            hint={comparado ? `${fmtInt(comparado.anuncios)} em ${rotuloJanela}` : 'Sem comparação'}
            deltaPct={variacaoPct(resumo.anuncios, comparado?.anuncios)}
            deltaSufixo={sufixo}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <KpiCard
            label="Unidades vendidas"
            value={v(fmtInt(resumo.unidades))}
            hint={comparado ? `${fmtInt(comparado.unidades)} em ${rotuloJanela}` : 'Sem comparação'}
            deltaPct={variacaoPct(resumo.unidades, comparado?.unidades)}
            deltaSufixo={sufixo}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <KpiCard
            label="Valor de vendas"
            value={v(fmtBrl(resumo.receita))}
            hint={comparado ? `${fmtBrl(comparado.receita)} em ${rotuloJanela}` : 'Sem comparação'}
            deltaPct={variacaoPct(resumo.receita, comparado?.receita)}
            deltaSufixo={sufixo}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <KpiCard
            label="Preço médio"
            value={v(resumo.precoMedio == null ? '—' : fmtBrl(resumo.precoMedio))}
            hint="Valor de vendas ÷ unidades vendidas"
            deltaPct={variacaoPct(resumo.precoMedio ?? 0, comparado?.precoMedio)}
            deltaSufixo={sufixo}
            loading={loading}
          />
        </Grid>
      </Grid>

      {!loading && !error ? (
        <Box sx={{ mb: 2 }}>
          <PropostaIaCard
            title="Leitura das Vendas por Anúncio"
            caption="Verdade = KPIs e tabela. Proposta = leitura da IA (ou resumo local se o LLM falhar) — revise antes de agir."
            state={leituraIa}
            onRetry={() => void carregarLeitura()}
          />
        </Box>
      ) : null}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 1.5 }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          size="small"
          label="Buscar"
          placeholder="Título do anúncio"
          value={draftBusca}
          onChange={(e) => setDraftBusca(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') aplicarBusca()
          }}
          sx={{ minWidth: { sm: 280 } }}
        />
        <Button size="small" variant="contained" onClick={aplicarBusca}>
          Buscar
        </Button>
        {busca ? (
          <Chip
            size="small"
            label={`filtro: ${busca}`}
            onDelete={() => {
              setDraftBusca('')
              setBusca('')
              setPagina(1)
            }}
          />
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          select
          size="small"
          label="Ordenar por"
          value={ordem}
          onChange={(e) => setOrdem(e.target.value as Ordem)}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="receita">Valor de vendas</MenuItem>
          <MenuItem value="quantidade">Unidades vendidas</MenuItem>
        </TextField>
      </Stack>

      <Card>
        <TableContainer sx={{ maxHeight: 620 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 44 }} />
                <TableCell sx={{ fontWeight: 700 }}>Anúncio</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>ID do anúncio</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  Pedidos válidos
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={ordem === 'quantidade'}
                    direction="desc"
                    onClick={() => setOrdem('quantidade')}
                  >
                    Unidades vendidas
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={ordem === 'receita'}
                    direction="desc"
                    onClick={() => setOrdem('receita')}
                  >
                    Valor de vendas
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  Preço médio
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow
                sx={{
                  '& td': {
                    fontWeight: 700,
                    bgcolor: 'action.hover',
                    position: 'sticky',
                    top: 37,
                    zIndex: 1,
                  },
                }}
              >
                <TableCell />
                <TableCell>Resumo</TableCell>
                <TableCell>—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">{v(fmtInt(resumo.unidades))}</TableCell>
                <TableCell align="right">{v(fmtBrl(resumo.receita))}</TableCell>
                <TableCell align="right">
                  {v(resumo.precoMedio == null ? '—' : fmtBrl(resumo.precoMedio))}
                </TableCell>
              </TableRow>

              {linhas.map((l) => (
                <Fragment key={l.id || l.descricao}>
                <TableRow hover>
                  <TableCell sx={{ width: 44, pr: 0 }}>
                    {l.id ? (
                      <IconButton
                        size="small"
                        onClick={() => alternarVariacoes(l.id)}
                        aria-label={aberto === l.id ? 'Fechar variações' : 'Ver variações'}
                        title="Ver o detalhe por variação deste anúncio"
                      >
                        {aberto === l.id ? '▾' : '▸'}
                      </IconButton>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 460 }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          flexShrink: 0,
                          borderRadius: 1,
                          overflow: 'hidden',
                          bgcolor: 'action.hover',
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        {l.thumbnail ? (
                          <Box
                            component="img"
                            src={l.thumbnail}
                            alt=""
                            loading="lazy"
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          // Sem capa na fonte: quadrado vazio. Nunca a imagem de outro anúncio.
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap title={l.titulo || l.descricao}>
                          {l.titulo || l.descricao}
                        </Typography>
                        {/* Só SKU do anúncio e canal. `sku_description` de `margens` fica de
                            fora: é a lista de SKUs do pedido e, em kit, os componentes — outro
                            nível, e foi o que deixou esta coluna confusa. */}
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {[l.sku, ...l.canais].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {!l.id ? (
                      '—'
                    ) : l.link ? (
                      // Abre o anúncio no canal. `noreferrer` porque é saída para fora do BI.
                      <Typography
                        component="a"
                        href={l.link}
                        target="_blank"
                        rel="noreferrer"
                        variant="body2"
                        color="primary"
                        title="Abrir o anúncio no marketplace"
                        sx={{ textDecoration: 'none', fontWeight: 600 }}
                      >
                        {l.id} ↗
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        title="Sem link: a Shopee precisa do id da loja, que não vem na fonte"
                      >
                        {l.id}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtInt(l.pedidos)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtInt(l.unidades)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtBrl(l.receita)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {l.precoMedio == null ? '—' : fmtBrl(l.precoMedio)}
                  </TableCell>
                </TableRow>

                {aberto === l.id ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ bgcolor: 'action.hover', py: 1.5 }}>
                      <PainelVariacoes estado={variacoes[l.id]} />
                    </TableCell>
                  </TableRow>
                ) : null}
                </Fragment>
              ))}

              {!loading && !linhas.length ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nenhum anúncio com venda neste período
                      {busca ? ` para "${busca}"` : ''}
                      {marketplace ? ` em ${canalLabel}` : ''}.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <CardContent
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {fmtInt(resumo.anuncios)} anúncio(s) com venda · {POR_PAGINA} por página · a linha
            Resumo é do período inteiro, não só desta página
            {excluidos ? (
              <>
                {' · '}
                <Box component="span" sx={{ color: 'warning.main' }}>
                  não incluídos: {fmtInt(excluidos.pedidos)} pedido(s) com mais de um anúncio
                  ({fmtBrl(excluidos.receita)}) — a base guarda o total do pedido, não o valor por
                  anúncio
                </Box>
              </>
            ) : null}
          </Typography>
          <Pagination
            size="small"
            count={totalPaginas}
            page={pagina}
            onChange={(_, p) => setPagina(p)}
            disabled={loading}
          />
        </CardContent>
      </Card>
    </DomainPageShell>
  )
}
